import { datadogRum } from '@datadog/browser-rum';
import { Typography } from '@mui/material';
import { useSession } from '@side/next-auth/react';
import {
  Dialog,
  DialogActionsWithButtons,
  DialogContent,
  usePersistedDismissable,
} from '@side/pantry';
import { useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';
import {
  TrackNotificationsEvent,
  useTrackNotificationsAnalytics,
} from '$analytics/useTrackAnalytics/domains/notifications';

type OneSignalSubscription = {
  id: string;
  optedIn: boolean;
  token: string;
};

type OneSignalInfo = {
  appId: string;
  appVersion: string;
  appVersionCode: number;
  distribution: string;
  deviceName: string;
  externalId: string;
  hardware: string;
  installationId: string;
  isFirstLaunch: boolean;
  language: string;
  legacy: boolean;
  model: string;
  oneSignalId: string;
  os: string;
  osVersion: string;
  platform: string;
  publicKey: string;
  requiresUserPrivacyConsent: boolean;
  subscription: OneSignalSubscription;
  timeZone: string;
  userConsentGiven: boolean;
} & Record<string, string | boolean | number>;

type DeviceInfo = {
  platform: string; // 'ios',
  appId: string; // 'io.median.example',
  appVersion: string; // '1.0.0',
  appBuild: string; // '1.0.0', // will be appVersionCode (number) on Android
  carrierNames: string[]; // ['AT&T'], // array of all connected mobile carriers
  distribution: string; // 'release',
  hardware: string; // 'armv8',
  installationId: string; // 'xxxx-xxxx-xxxx-xxxx',
  apnsToken: string; // '<xxxxxxxx xxxxxxxx xxxxxxxx ... >', // only on iOS
  androidSha1: string; // 'XX:XX:XX:XX:XX:XX:XX:XX:XX:XX:XX:XX', // only on Android
  language: string; // 'en',
  model: string; // 'iPhone',
  os: string; // 'iOS',
  osVersion: string; // '10.3',
  timeZone: string; // 'America/New_York',
  isFirstLaunch: boolean; // first time the app is launched
};

interface Median {
  share: {
    /**
     * Download file from URL
     * @see https://median.co/docs/download-file
     * @param options - options object
     * @param options.url - URL to download file from
     * @param options.filename - Suggested filename for the downloaded file
     * @param options.open - Android only - shows open with dialog after download (matching IOS)
     */
    downloadFile: (options: {
      url: string;
      filename?: string;
      open?: boolean;
    }) => void;
  };
  /**
   * Get device info
   * NOTE: Currently optional since newer versions of native app (built with Cordova) do not have this method
   * @see https://median.co/docs/device-info
   */
  deviceInfo?: () => Promise<DeviceInfo>;
  onesignal: {
    /**
     * Get OneSignal info - used to determine if device has already been registered with push
     * @see https://median.co/docs/onesignal-info
     */
    onesignalInfo: () => Promise<OneSignalInfo>;
    /**
     * Register device for push notifications
     * @see https://median.co/docs/push-registration-and-privacy-consent
     */
    register: () => void;
    externalUserId: {
      /**
       * Set external user ID within OneSignal
       * @see https://median.co/docs/personalized-push#2---use-external-user-ids-on-the-onesignal-platform
       */
      set: ({ externalId }: { externalId: string }) => void;
    };
  };
}

declare global {
  interface Window {
    median?: Median;
  }
}

/**
 * Identify the user for push notifications and display push
 * permissions primer dialog
 * @see https://median.co/docs/personalized-push
 */
export function SetupPushNotifications() {
  const { id } = useSession()?.data?.user ?? {};
  const trackEvent = useTrackNotificationsAnalytics();
  const [isOpen, setOpen] = useState(false);
  const [shouldShow, { dismiss }] = usePersistedDismissable({
    key: 'notification-permission-dialog',
  });

  const handleClose = () => {
    setOpen(false);
    trackEvent(TrackNotificationsEvent.AuthorizeNotificationsDismissed);
    dismiss();
  };

  const handleAllow = () => {
    // Close before opening system dialog for notification allow
    // since we don't have a way to listen for completion of system dialog
    setOpen(false);
    trackEvent(TrackNotificationsEvent.ClickAuthorizeNotifications);
    // Dismiss so we don't re-prompt - we assume that they don't want to be
    // reprompted even if they select "Don't Allow" in system dialog
    dismiss();
    // Open system notification permissions dialog
    window.median?.onesignal.register();
  };

  useEffect(() => {
    // Exit if median is not defined on window (i.e. not running in mobile app wrapper) or
    // if median.onesignal is not defined (i.e. not mobile app version with push notifications through onesignal) or
    // if user is not authenticated (to prevent showing on unauthenticated pages)
    // NOTE: Feature flag is not used because if push notifications are enabled, we want to set the user id
    if (typeof window.median?.onesignal === 'undefined' || !id) {
      return undefined;
    }

    // Set user within onesignal with externalId as identityId
    datadogRum.addAction('OneSignal set externalUserId', { id });
    window.median.onesignal.externalUserId.set({
      externalId: id,
    });

    // Only open dialog if flag enabled and dialog hasn't already been closed previously on this device
    if (shouldShow) {
      let openTimeout: NodeJS.Timeout | undefined;
      void window.median.onesignal.onesignalInfo().then((oneSignalInfo) => {
        // NOTE: Consoles left intentionally while testing
        // biome-ignore lint/suspicious/noConsole: Used for debugging
        console.log('One signal info', oneSignalInfo);
        if (!oneSignalInfo.subscription.optedIn) {
          // Wait to open dialog until styles have loaded (see PLAT-8855)
          openTimeout = setTimeout(() => {
            setOpen(true);
          }, 150);
        } else {
          // Dismiss if user has opted in so that modal doesn't attempt to re-open. We don't dismiss
          // on allow because "deny" could still be clicked in system popup
          dismiss();
        }
      });
      // Cleanup openTimeout on unmount if set
      return () => {
        if (openTimeout) {
          clearTimeout(openTimeout);
        }
      };
    }
    return undefined;
  }, [id, shouldShow, dismiss]);

  useEffectOnce(() => {
    // Set app version into DD context
    if (window.median?.deviceInfo) {
      void window.median.deviceInfo().then((deviceInfo) => {
        datadogRum.setGlobalContextProperty(
          'native_app_version',
          deviceInfo.appVersion,
        );
      });
    }
  });

  // Render nothing if not on native or feature flag is not enabled
  // used since otherwise Dialog would be added to the DOM and never rendered
  if (typeof window.median?.onesignal === 'undefined') {
    return null;
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullScreen={false}>
      <DialogContent
        title="Personalize your notifications"
        onClose={handleClose}
      >
        <Typography>
          Get alerts for key events like getting paid, offers accepted,
          time-sensitive action items, and more!
        </Typography>
      </DialogContent>
      <DialogActionsWithButtons
        SubmitButtonProps={{
          defaultText: 'Allow Notifications',
          onClick: handleAllow,
        }}
        CancelButtonProps={{ onClick: handleClose }}
      />
    </Dialog>
  );
}
